import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { litterProfiles } from '../data/litterProfiles';
import { upcomingLitters } from '../data/upcomingLitters';
import PuppyGalleryModal from './puppies/PuppyGalleryModal';
import { trackEvent, trackLink } from '../utils/matomoEvents';
import { byDisplayOrder, litterAnchorId } from '../utils/litterNav';
import { photoPosition } from '../utils/photoPosition';

// A parent may be one of our own dogs (links to its profile page) or an
// outside dog from a stud-out/pup-back deal, which has no profile here and
// instead links to the breeder who owns it.
function ParentCard({ parent, role, headingLevel = 'h3', onClick }) {
  const image = (
    <div
      className="parent-image"
      style={{
        backgroundImage: `url(${parent.image})`,
        ...photoPosition(
          parent.availablePuppies_backgroundPosition,
          parent.availablePuppies_mobile_backgroundPosition
        )
      }}
    ></div>
  );
  const Heading = headingLevel;
  const heading = <Heading>{role}: {parent.name}</Heading>;

  if (parent.external) {
    return (
      <a
        href={parent.breederUrl}
        className="parent-card"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
      >
        {image}
        {heading}
        <p className="parent-breeder">
          Owned by {parent.breeder}
          <span className="parent-breeder-cta">Visit their page</span>
        </p>
      </a>
    );
  }

  return (
    <Link to={parent.path} className="parent-card" onClick={onClick}>
      {image}
      {heading}
      <p>{parent.short_bio}</p>
    </Link>
  );
}

// A parent card either goes to one of our own profile pages or off the site to
// the breeder who owns the dog. The second kind is an outbound link, so it is
// recorded the same way the social links are: an event plus trackLink, which is
// what puts it in Matomo's Outlinks report.
function trackParentClick(parentType, parent, litterLabel) {
  trackEvent(
    'Puppies',
    parent.external ? 'ViewExternalParentBreeder' : 'ViewParentProfile',
    `${parentType} - ${parent.name} (${litterLabel})`
  );
  if (parent.external && parent.breederUrl) {
    trackLink(parent.breederUrl);
  }
}

function LitterParents({ mother, father, litterTitle }) {
  const handleParentClick = (parentType, parent) => {
    trackParentClick(parentType, parent, litterTitle);
  };

  return (
    <div className="parents-container">
      <ParentCard
        parent={mother}
        role="Mother"
        onClick={() => handleParentClick('Mother', mother)}
      />
      <ParentCard
        parent={father}
        role="Father"
        onClick={() => handleParentClick('Father', father)}
      />
    </div>
  );
}

// A puppy whose price came down shows the old price struck through, why it
// changed, and a link to the health page that explains the reason in full.
function PriceReduction({ puppy }) {
  const { original_price, reason, learn_more } = puppy.price_reduction;

  // The card itself opens the gallery, so keep this click from doing that too.
  const handleLearnMoreClick = (e) => {
    e.stopPropagation();
    trackEvent('Puppies', 'ViewPriceReductionReason', puppy.name);
  };

  return (
    <div className="price-reduction">
      <span className="price-reduced-badge">Price Reduced</span>
      <p className="price-reduced-prices">
        Pet Price: <s className="price-original">{original_price}</s>{' '}
        <strong className="price-current">{puppy.pet_price}</strong>
      </p>
      {reason && <p className="price-reduced-reason">{reason}</p>}
      {learn_more && (
        <Link to={learn_more} className="price-reduced-link" onClick={handleLearnMoreClick}>
          Learn more about this
        </Link>
      )}
    </div>
  );
}

function LitterSection({ litter, onPuppyClick }) {
  const [puppyImages, setPuppyImages] = useState({});
  const [puppyImageCounts, setPuppyImageCounts] = useState({}); // Track the number of images per puppy

  // Track litter section view on mount
  useEffect(() => {
    trackEvent('Puppies', 'ViewLitter', litter.title, litter.availablePuppies.length);
  }, [litter.title, litter.availablePuppies.length]);

  // Handle puppy card click with tracking
  const handlePuppyCardClick = (puppy) => {
    trackEvent('Puppies', 'ViewPuppyCard', `${puppy.name} - ${puppy.status}`, puppy.pet_price ? parseInt(puppy.pet_price.replace(/[^0-9]/g, '')) : 0);
    onPuppyClick({ ...puppy, imageCount: puppyImageCounts[puppy.id] });
  };

  useEffect(() => {
    const fetchPuppyImages = async () => {
      const images = {};
      const imageCounts = {}; // Store image counts
      for (const puppy of litter.availablePuppies) {
        if (puppy.photo_index) {
          try {
            const response = await fetch(puppy.photo_index);
            if (response.ok) {
              const data = await response.json();
              images[puppy.id] = data.photos.find(photo => photo.includes('_P')) || puppy.image;
              imageCounts[puppy.id] = data.photos.length; // Store the count of photos
            } else {
              images[puppy.id] = puppy.image;
              imageCounts[puppy.id] = 1; // Default to 1 if fetch fails
            }
          } catch (error) {
            console.error(`Error fetching photo index for puppy ${puppy.name}:`, error);
            images[puppy.id] = puppy.image;
            imageCounts[puppy.id] = 1; // Default to 1 on error
          }
        } else {
          images[puppy.id] = puppy.image;
          imageCounts[puppy.id] = 1; // Default to 1 if no photo index
        }
      }
      setPuppyImages(images);
      setPuppyImageCounts(imageCounts); // Update state with image counts
    };

    fetchPuppyImages();
  }, [litter.availablePuppies]);

  return (
    <section className="litter-section" id={litterAnchorId(litter)}>
      <div className="litter-header">
        <h2>{litter.title}</h2>
        <div className="litter-dates">
          <p>Birth Date: {litter.birthDate}</p>
          <p>Ready Date: {litter.readyDate}</p>
        </div>
      </div>
      <LitterParents mother={litter.parents.mother} father={litter.parents.father} litterTitle={litter.title} />
      <div className="puppy-grid">
        {litter.availablePuppies.map(puppy => {
          // photo_index is absent until a puppy's gallery exists, so only
          // derive the base path when there is one to derive it from.
          const puppyImage = puppy.photo_index && puppyImages[puppy.id]
            ? `${puppy.photo_index.replace('/index.json', '')}/${puppyImages[puppy.id]}`
            : puppy.image;
          return (
            <div
              key={puppy.id}
              className="puppy-card"
              style={{
                backgroundColor: puppy.status === 'Unavailable' ? 'var(--unavailable-bg)' : 'var(--surface)',
                cursor: 'pointer'
              }}
              onClick={() => handlePuppyCardClick(puppy)}
            >
              <div className="puppy-image" style={{
                backgroundImage: `url(${puppyImage})`,
                backgroundSize: 'cover',
                backgroundPosition: puppy.backgroundPosition,
                filter: puppy.status === 'Unavailable' ? 'grayscale(80%) brightness(0.8)' : 'none'
              }}></div>
              <h3 style={{ color: puppy.status === 'Unavailable' ? 'var(--unavailable-text)' : 'inherit' }}>{puppy.name}</h3>
              <p style={{ color: puppy.status === 'Unavailable' ? 'var(--unavailable-text)' : 'inherit' }}>
                {/* Details trickle in as a litter grows, so skip any field we
                    do not have a value for rather than print a bare label. */}
                {puppy.gender && <>Gender: {puppy.gender}<br /></>}
                {puppy.markings && <>Markings: {puppy.markings}<br /></>}
                {puppy.status && <>Status: <span style={{ color: puppy.status === 'Unavailable' ? 'var(--unavailable-status)' : 'inherit' }}>{puppy.status}</span><br /></>}
                {puppy.pet_price && !puppy.price_reduction && <>Pet Price: {puppy.pet_price}</>}
              </p>
              {puppy.price_reduction && (
                <PriceReduction puppy={puppy} />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function UpcomingLitterCard({ litter }) {
  const handleParentClick = (parentType, parent) => {
    trackParentClick(parentType, parent, `${litter.title} - Upcoming`);
  };

  return (
    <div className="upcoming-litter-card">
      <div className="litter-header">
        <h3>{litter.title}</h3>
        <div className="litter-dates">
          <p>Expected Birth: {litter.expectedBirthDate}</p>
          <p>Expected Ready: {litter.expectedReadyDate}</p>
        </div>
      </div>
      <div className="parents-container">
        <ParentCard
          parent={litter.parents.mother}
          role="Mother"
          headingLevel="h4"
          onClick={() => handleParentClick('Mother', litter.parents.mother)}
        />
        <ParentCard
          parent={litter.parents.father}
          role="Father"
          headingLevel="h4"
          onClick={() => handleParentClick('Father', litter.parents.father)}
        />
      </div>
    </div>
  );
}

function UpcomingLittersSection() {
  if (upcomingLitters.length === 0) {
    return null;
  }

  return (
    <section className="upcoming-litters-section">
      <h2>Upcoming Litters</h2>
      <div className="upcoming-litters-grid">
        {byDisplayOrder(upcomingLitters).map(litter => (
          <UpcomingLitterCard key={litter.id} litter={litter} />
        ))}
      </div>
    </section>
  );
}

export default function AvailablePuppies() {
  const [selectedPuppy, setSelectedPuppy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { hash } = useLocation();
  const navigationType = useNavigationType();
  // The hash the visitor arrived with, kept because later ones come from the
  // header menu rather than from a link they followed into the site.
  const arrivalHash = useRef(hash);

  // The header's litter menu links here with a #litter-<id> hash. A router
  // navigation does not scroll on its own, so do it here - and re-run on every
  // hash change, since picking another litter while already on this page does
  // not remount the component. `.litter-section` carries a scroll-margin-top
  // to clear the fixed header.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  // Someone following a shared /puppies#litter-<id> link - from a Facebook post
  // or a bookmark - lands on one litter in particular, which is worth knowing.
  // Only the hash they arrived with counts: a PUSH means they got here from
  // inside the site, where the header already tracks the menu click. The litter
  // is looked up from the data, so a new litter needs no new tracking code.
  useEffect(() => {
    if (!arrivalHash.current || navigationType === 'PUSH') return;
    const arrived = litterProfiles.litters.find(
      litter => litterAnchorId(litter) === arrivalHash.current.slice(1)
    );
    if (arrived) {
      trackEvent('Puppies', 'ArriveAtLitter', arrived.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePuppyClick = (puppy) => {
    setSelectedPuppy(puppy);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const hasNoLitters = litterProfiles.litters.length === 0 && upcomingLitters.length === 0;

  return (
    <div className="available-puppies">
      {hasNoLitters ? (
        <div className="empty-state">
          <h2>No Puppies Available</h2>
          <p>
            New litter information coming soon, please check back later or follow our{' '}
            <a href="https://www.facebook.com/GatorSkinFrenchies" target="_blank" rel="noopener noreferrer">
              Facebook page
            </a>{' '}
            for more info.
          </p>
        </div>
      ) : (
        <>
          {byDisplayOrder(litterProfiles.litters).map(litter => (
            <LitterSection
              key={litter.id}
              litter={litter}
              onPuppyClick={handlePuppyClick}
            />
          ))}
          <UpcomingLittersSection />
        </>
      )}
      {selectedPuppy && (
        <PuppyGalleryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          puppy={selectedPuppy}
          disableNavigation={selectedPuppy.imageCount === 1} // Disable navigation if only 1 image
        />
      )}
      <style jsx>{`
        .available-puppies {
          padding: 2rem;
        }
        .litter-section {
          scroll-margin-top: 110px;
          margin-bottom: 4rem;
          background-color: var(--surface-sunken);
          padding: 2rem;
          border-radius: 8px;
        }
        .litter-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        .litter-dates {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-top: 1rem;
        }
        .parents-container {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          margin-bottom: 2rem;
        }
        .parent-card {
          background: var(--surface);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px var(--shadow);
          text-decoration: none;
          color: inherit;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .parent-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 8px var(--shadow-strong);
        }
        .parent-image {
          height: 250px;
          background-size: cover;
          background-position: var(--photo-pos, center);
        }
        .parent-card h3 {
          padding: 1rem;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .parent-card p {
          padding: 1rem;
          margin: 0;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
        .parent-breeder {
          font-weight: 600;
          color: var(--heading);
        }
        .parent-breeder-cta {
          display: block;
          margin-top: 0.35rem;
          font-weight: 400;
          font-size: 0.9rem;
          color: var(--accent);
        }
        .puppy-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 300px));
          gap: 2rem;
          justify-content: center;
          margin: 0 auto;
          max-width: 1200px;
        }
        .puppy-card {
          width: 100%;
          max-width: 350px;
          padding: 1rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow);
        }
        .puppy-image {
          height: 250px;
          border-radius: 8px;
        }
        .puppy-card h3 {
          margin: 1rem 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .puppy-card p {
          margin: 0;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
        }
        .price-reduction {
          margin: 0.75rem 1rem 0;
          padding: 0.75rem 0 0.25rem;
          border-top: 1px solid var(--border-soft);
        }
        .price-reduced-badge {
          display: inline-block;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          background: var(--success);
          color: var(--text-on-accent);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        /* App.css pads every .puppy-card p; this block brings its own. */
        .puppy-card .price-reduction p {
          display: block;
          margin: 0.5rem 0 0;
          padding: 0;
        }
        .price-original {
          color: var(--text-faint);
        }
        .price-current {
          color: var(--text);
          font-size: 1.1rem;
        }
        .puppy-card .price-reduction .price-reduced-reason {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: 1.4;
        }
        .price-reduced-link {
          display: inline-block;
          margin-top: 0.4rem;
          color: var(--accent);
          font-size: 0.95rem;
          text-decoration: none;
        }
        .price-reduced-link:hover {
          text-decoration: underline;
        }
        .upcoming-litters-section {
          margin-top: 2rem;
          padding: 2rem;
          background-color: var(--surface-sunken);
          border-radius: 8px;
        }
        .upcoming-litters-section > h2 {
          text-align: center;
          margin-bottom: 2rem;
          color: var(--heading);
        }
        .upcoming-litters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 2rem;
        }
        .upcoming-litter-card {
          background: var(--surface);
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px var(--shadow);
        }
        .upcoming-litter-card .litter-header {
          margin-bottom: 1.5rem;
        }
        .upcoming-litter-card .litter-header h3 {
          margin: 0 0 0.5rem 0;
        }
        .upcoming-litter-card .litter-dates {
          margin-top: 0.5rem;
        }
        .upcoming-litter-card .parents-container {
          margin-bottom: 0;
        }
        .upcoming-litter-card .parent-card h4 {
          padding: 1rem;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background-color: var(--surface-sunken);
          border-radius: 8px;
        }
        .empty-state h2 {
          margin: 0 0 1rem 0;
          color: var(--heading);
        }
        .empty-state p {
          margin: 0;
          color: var(--text-muted);
          font-size: 1.1rem;
          line-height: 1.6;
        }
        .empty-state a {
          color: var(--accent);
          text-decoration: none;
        }
        .empty-state a:hover {
          text-decoration: underline;
        }
        @media (max-width: 768px) {
          .upcoming-litters-grid {
            grid-template-columns: 1fr;
          }
          /* Two parent cards side by side leave about 150px each on a phone,
             which squeezed "Mother: Tonks" down to "Mothe...". Stack them and
             claw back some of the padding the narrow screen cannot spare. */
          .parents-container {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          .available-puppies {
            padding: 1rem;
          }
          .litter-section {
            padding: 1.25rem;
            margin-bottom: 2.5rem;
          }
          .upcoming-litters-section {
            padding: 1.25rem;
          }
          .parent-image {
            height: 220px;
            /* availablePuppies_mobile_backgroundPosition, when a dog has one. */
            background-position: var(--photo-pos-mobile, var(--photo-pos, center));
          }
          .parent-card h3,
          .parent-card h4 {
            padding: 0.85rem 1rem 0;
          }
          .parent-card p {
            padding: 0.5rem 1rem 1rem;
            /* A full-width card has room for the whole short bio, so drop the
               3-line clamp that cut it off mid-sentence. */
            display: block;
            -webkit-line-clamp: unset;
          }
        }
      `}</style>
    </div>
  );
}